import { useState, useEffect } from 'react';
import {
  Button, Card, CardContent, CardHeader, CardTitle, Badge, Input, Label, Textarea,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Spinner,
} from '@rnd-payplus/alloy-react';
import { Plus, Pencil, Trash2, GripVertical, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import { usePageShell } from '../contexts/PageShellContext';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserStory {
  id: string;
  br_reference: string;
  capability: string;
  title: string;
  description: string;
  acceptance_criteria: string;
  original_br_id: string | null;
  persona_id: string | null;
  source: string;
  status: 'todo' | 'in_progress' | 'done';
  priority: 'low' | 'medium' | 'high' | 'critical';
  story_points: number | null;
  assigned_to: string | null;
  release_tag: string | null;
  notes: string | null;
}

interface EditDraft {
  priority?: UserStory['priority'];
  storyPoints?: number;
  assignedTo?: string;
  releaseTag?: string;
  notes?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CAPABILITIES = [
  'SC-01: Integration Design & Configuration',
  'SC-02: Universal Connectivity',
  'SC-03: Common Data Model & Master Data',
  'SC-04: Data Quality & Exception Management',
  'SC-05: Scalable Processing & Resilience',
  'SC-06: Operational Observability',
  'SC-07: Self-Service & Customer Enablement',
  'SC-08: Developer Ecosystem & API Governance',
  'SC-09: Security, Compliance & Data Sovereignty',
  'SC-10: Deployment Flexibility & Product Integration',
];

const CAPABILITY_COLORS: Record<string, string> = {
  'SC-01': '#1976d2', 'SC-02': '#388e3c', 'SC-03': '#7b1fa2',
  'SC-04': '#d32f2f', 'SC-05': '#f57c00', 'SC-06': '#0097a7',
  'SC-07': '#5d4037', 'SC-08': '#455a64', 'SC-09': '#c62828',
  'SC-10': '#2e7d32',
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: '#d32f2f', high: '#f57c00', medium: '#1976d2', low: '#757575',
};

const COLUMNS: Array<{ key: 'todo' | 'in_progress' | 'done'; label: string }> = [
  { key: 'todo', label: 'To Do' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'done', label: 'Done' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function capabilityCode(cap: string): string {
  return cap.split(':')[0]?.trim() ?? cap;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ProductBacklogPage = () => {
  const [stories, setStories] = useState<UserStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [capabilityFilter, setCapabilityFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedStory, setSelectedStory] = useState<UserStory | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDraft, setEditDraft] = useState<EditDraft>({});
  const [saving, setSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newStory, setNewStory] = useState({
    brReference: '',
    capability: CAPABILITIES[0],
    title: '',
    description: '',
    acceptanceCriteria: '',
    personaId: '',
    priority: 'medium' as const,
  });

  const loadStories = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (capabilityFilter) params.set('capability', capabilityFilter);
      if (sourceFilter) params.set('source', sourceFilter);
      if (search) params.set('search', search);
      const res = await fetch(`/api/user-stories?${params}`);
      setStories(await res.json());
    } catch (err) {
      console.error('Failed to load stories', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStories(); }, [capabilityFilter, sourceFilter, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const storiesByStatus = (status: UserStory['status']) =>
    stories.filter(s => s.status === status);

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const newStatus = result.destination.droppableId as UserStory['status'];
    const storyId = result.draggableId;

    // Optimistic update
    setStories(prev => prev.map(s => s.id === storyId ? { ...s, status: newStatus } : s));

    try {
      await fetch(`/api/user-stories/${storyId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
    } catch {
      loadStories(); // revert on failure
    }
  };

  const openPanel = (story: UserStory) => {
    setSelectedStory(story);
    setEditDraft({
      priority: story.priority,
      storyPoints: story.story_points ?? undefined,
      assignedTo: story.assigned_to ?? '',
      releaseTag: story.release_tag ?? '',
      notes: story.notes ?? '',
    });
    setPanelOpen(true);
  };

  const savePanel = async () => {
    if (!selectedStory) return;
    setSaving(true);
    try {
      await fetch(`/api/user-stories/${selectedStory.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editDraft),
      });
      await loadStories();
      setPanelOpen(false);
      toast.success('Story updated');
    } catch {
      toast.error('Failed to save story');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteStory = async () => {
    if (!selectedStory) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/user-stories/${selectedStory.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        throw new Error(`Failed to delete story (${res.status})`);
      }
      await loadStories();
      setDeleteDialogOpen(false);
      setPanelOpen(false);
      toast.success('Story deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete story');
    } finally {
      setSaving(false);
    }
  };

  const addStory = async () => {
    try {
      await fetch('/api/user-stories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newStory, source: 'Custom', status: 'todo' }),
      });
      setAddDialogOpen(false);
      setNewStory({
        brReference: '',
        capability: CAPABILITIES[0],
        title: '',
        description: '',
        acceptanceCriteria: '',
        personaId: '',
        priority: 'medium',
      });
      loadStories();
      toast.success('Story added');
    } catch (err) {
      console.error('Failed to add story', err);
      toast.error('Failed to add story');
    }
  };

  const totalStories = stories.length;
  const todoCount = stories.filter(s => s.status === 'todo').length;
  const inProgressCount = stories.filter(s => s.status === 'in_progress').length;
  const doneCount = stories.filter(s => s.status === 'done').length;

  usePageShell({
    title: 'Product Backlog',
    primaryAction: (
      <Button onClick={() => setAddDialogOpen(true)}>
        <Plus className="h-4 w-4 mr-2" />
        Add Story
      </Button>
    ),
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Product Backlog' },
    ],
  });

  return (
    <div className="h-full flex flex-col">
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4 mb-3">
        <KPICard title="Total" value={totalStories} subtitle="Stories" />
        <KPICard title="To Do" value={todoCount} subtitle="Stories" />
        <KPICard title="In Progress" value={inProgressCount} subtitle="Stories" />
        <KPICard title="Done" value={doneCount} subtitle="Stories" />
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <div className="relative w-[280px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search stories..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={capabilityFilter || '_all'}
          onValueChange={v => setCapabilityFilter(v === '_all' ? '' : v)}
        >
          <SelectTrigger className="w-[280px]">
            <SelectValue placeholder="All Capabilities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Capabilities</SelectItem>
            {CAPABILITIES.map(c => (
              <SelectItem key={c} value={c}>{capabilityCode(c)}: {c.split(': ')[1]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={sourceFilter || '_all'}
          onValueChange={v => setSourceFilter(v === '_all' ? '' : v)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All</SelectItem>
            <SelectItem value="Original">Original</SelectItem>
            <SelectItem value="New">New</SelectItem>
            <SelectItem value="Custom">Custom</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Capability summary bar */}
      <div className="flex gap-1 mb-2 flex-wrap">
        {CAPABILITIES.map(cap => {
          const code = capabilityCode(cap);
          const count = stories.filter(s => s.capability === cap).length;
          const isActive = capabilityFilter === cap;
          return (
            <button
              key={cap}
              onClick={() => setCapabilityFilter(capabilityFilter === cap ? '' : cap)}
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[0.7rem] font-medium transition-colors cursor-pointer border"
              style={{
                backgroundColor: isActive ? CAPABILITY_COLORS[code] : undefined,
                color: isActive ? 'white' : undefined,
                borderColor: isActive ? CAPABILITY_COLORS[code] : 'hsl(var(--border))',
              }}
            >
              {code} ({count})
            </button>
          );
        })}
      </div>

      {/* Kanban board */}
      {loading ? (
        <div className="flex justify-center mt-8">
          <Spinner />
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-3 gap-2 flex-1 min-h-0">
            {COLUMNS.map(col => (
              <div key={col.key} className="flex flex-col min-h-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <h3 className="text-sm font-semibold">{col.label}</h3>
                  <Badge variant="secondary">{storiesByStatus(col.key).length}</Badge>
                </div>
                <Droppable droppableId={col.key}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 overflow-y-auto rounded-lg p-1 min-h-[120px] transition-colors ${
                        snapshot.isDraggingOver ? 'bg-accent' : 'bg-background'
                      }`}
                    >
                      {storiesByStatus(col.key).map((story, index) => (
                        <Draggable key={story.id} draggableId={story.id} index={index}>
                          {(drag, dragSnapshot) => (
                            <Card
                              ref={drag.innerRef}
                              {...drag.draggableProps}
                              {...drag.dragHandleProps}
                              onClick={() => openPanel(story)}
                              className={`mb-1.5 cursor-pointer hover:shadow-md transition-shadow ${
                                dragSnapshot.isDragging ? 'opacity-85' : ''
                              }`}
                              style={{
                                ...drag.draggableProps.style,
                                borderLeft: `4px solid ${PRIORITY_COLORS[story.priority]}`,
                              }}
                            >
                              <CardContent className="p-2.5">
                                <div className="flex justify-between mb-0.5">
                                  <Badge variant="secondary" className="text-[0.65rem] h-[18px]">
                                    {story.br_reference}
                                  </Badge>
                                  {story.source === 'New' && (
                                    <Badge className="text-[0.65rem] h-[18px]">New</Badge>
                                  )}
                                </div>
                                <p className="text-xs leading-snug mb-1 line-clamp-2">
                                  {story.title}
                                </p>
                                <div className="flex gap-0.5 flex-wrap">
                                  <Badge
                                    className="text-[0.6rem] h-4 text-white"
                                    style={{ backgroundColor: CAPABILITY_COLORS[capabilityCode(story.capability)] }}
                                  >
                                    {capabilityCode(story.capability)}
                                  </Badge>
                                  {story.persona_id?.split(',').slice(0, 2).map(p => (
                                    <Badge key={p} variant="secondary" className="text-[0.6rem] h-4">
                                      {p.trim()}
                                    </Badge>
                                  ))}
                                  {story.story_points && (
                                    <Badge variant="outline" className="text-[0.6rem] h-4">
                                      {story.story_points}sp
                                    </Badge>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>
      )}

      {/* Detail side panel (as Dialog) */}
      <Dialog open={panelOpen} onOpenChange={setPanelOpen}>
        <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto">
          {selectedStory && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{selectedStory.br_reference}</Badge>
                  {selectedStory.original_br_id && (
                    <Badge variant="outline">{selectedStory.original_br_id}</Badge>
                  )}
                </div>
                <DialogTitle className="text-base font-semibold leading-snug">
                  {selectedStory.title}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Edit story details
                </DialogDescription>
              </DialogHeader>

              <Badge
                className="self-start text-white"
                style={{ backgroundColor: CAPABILITY_COLORS[capabilityCode(selectedStory.capability)] }}
              >
                {selectedStory.capability}
              </Badge>

              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold mb-1">Description</h4>
                  <p className="text-sm text-muted-foreground">{selectedStory.description}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-1">Acceptance Criteria</h4>
                  <p className="text-sm text-muted-foreground">{selectedStory.acceptance_criteria}</p>
                </div>

                {selectedStory.persona_id && (
                  <div>
                    <h4 className="text-sm font-semibold mb-1">Personas</h4>
                    <div className="flex gap-1 flex-wrap">
                      {selectedStory.persona_id.split(',').map(p => (
                        <Badge key={p} variant="secondary">{p.trim()}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t pt-3 mt-3">
                <h4 className="text-sm font-semibold mb-3">Edit</h4>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="space-y-1.5">
                    <Label>Priority</Label>
                    <Select
                      value={editDraft.priority ?? selectedStory.priority}
                      onValueChange={v => setEditDraft(d => ({ ...d, priority: v as UserStory['priority'] }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="critical">Critical</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Story Points</Label>
                    <Input
                      type="number"
                      value={editDraft.storyPoints ?? ''}
                      onChange={e => setEditDraft(d => ({ ...d, storyPoints: parseInt(e.target.value) || undefined }))}
                    />
                  </div>
                </div>

                <div className="space-y-1.5 mb-3">
                  <Label>Assigned To</Label>
                  <Input
                    value={editDraft.assignedTo ?? ''}
                    onChange={e => setEditDraft(d => ({ ...d, assignedTo: e.target.value }))}
                  />
                </div>

                <div className="space-y-1.5 mb-3">
                  <Label>Release Tag</Label>
                  <Input
                    value={editDraft.releaseTag ?? ''}
                    onChange={e => setEditDraft(d => ({ ...d, releaseTag: e.target.value }))}
                  />
                </div>

                <div className="space-y-1.5 mb-3">
                  <Label>Notes</Label>
                  <Textarea
                    rows={3}
                    value={editDraft.notes ?? ''}
                    onChange={e => setEditDraft(d => ({ ...d, notes: e.target.value }))}
                  />
                </div>
              </div>

              <DialogFooter className="gap-2 sm:justify-between">
                <Button
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={saving}
                  title="Delete story"
                  aria-label="Delete story"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
                <div className="flex gap-2">
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button onClick={savePanel} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Story dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Add Story</DialogTitle>
            <DialogDescription>
              Create a new user story for the product backlog.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>BR Reference</Label>
              <Input
                placeholder="e.g. BR051"
                value={newStory.brReference}
                onChange={e => setNewStory(s => ({ ...s, brReference: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Capability</Label>
              <Select
                value={newStory.capability}
                onValueChange={v => setNewStory(s => ({ ...s, capability: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAPABILITIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>User Story</Label>
              <Textarea
                rows={2}
                placeholder="As a [persona], I want to... so that..."
                value={newStory.title}
                onChange={e => setNewStory(s => ({ ...s, title: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                rows={2}
                value={newStory.description}
                onChange={e => setNewStory(s => ({ ...s, description: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Acceptance Criteria</Label>
              <Textarea
                rows={2}
                value={newStory.acceptanceCriteria}
                onChange={e => setNewStory(s => ({ ...s, acceptanceCriteria: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Persona IDs (e.g. RD-D, BIT-U)</Label>
              <Input
                value={newStory.personaId}
                onChange={e => setNewStory(s => ({ ...s, personaId: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={addStory} disabled={!newStory.title || !newStory.brReference}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete story</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          {selectedStory && (
            <p className="py-2 text-sm">
              Are you sure you want to delete{' '}
              <span className="font-medium">&ldquo;{selectedStory.title}&rdquo;</span>?
            </p>
          )}
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" disabled={saving}>Cancel</Button>
            </DialogClose>
            <Button variant="destructive" onClick={confirmDeleteStory} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductBacklogPage;
